import { Howl, Howler } from 'howler';

type SoundName = 'click' | 'correct' | 'perfect' | 'zero' | 'rewind' | 'hint' | 'achievement';

class AudioManager {
  private sounds: Map<SoundName, Howl> = new Map();
  private volume: number = 0.8;
  private enabled: boolean = true;
  private audioUnlocked: boolean = false;

  constructor() {
    this.loadSounds();
    this.setupIOSUnlock();
  }

  private loadSounds(): void {
    const soundFiles: Record<SoundName, string> = {
      click: '/sounds/sound_click.wav',
      correct: '/sounds/sound_correct.wav',
      perfect: '/sounds/sound_10correct.wav',
      zero: '/sounds/sound_0correct.wav',
      rewind: '/sounds/sound_rewind.wav',
      hint: '/sounds/sound_hint.wav',
      achievement: '/sounds/sound_10correct.wav',
    };

    Object.entries(soundFiles).forEach(([key, src]) => {
      const soundName = key as SoundName;
      this.sounds.set(
        soundName,
        new Howl({
          src: [src],
          volume: this.volume,
          preload: true,
          html5: false, // Use Web Audio API for better performance
        })
      );
    });
  }

  /**
   * iOS requires a user gesture (touchstart/click) to unlock the AudioContext.
   * We listen for the first interaction and resume the context + play a silent buffer.
   */
  private setupIOSUnlock(): void {
    const unlockHandler = (): void => {
      this.unlockAudio();
      // Remove listeners after first unlock
      document.removeEventListener('touchstart', unlockHandler, true);
      document.removeEventListener('touchend', unlockHandler, true);
      document.removeEventListener('click', unlockHandler, true);
    };

    document.addEventListener('touchstart', unlockHandler, true);
    document.addEventListener('touchend', unlockHandler, true);
    document.addEventListener('click', unlockHandler, true);
  }

  play(sound: SoundName): void {
    if (!this.enabled) return;

    // Ensure audio is unlocked (fallback for non-iOS paths)
    if (!this.audioUnlocked) {
      this.unlockAudio();
    }

    const howl = this.sounds.get(sound);
    if (howl) {
      howl.play();
    }
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.volume);

    // Update volume for all loaded sounds
    this.sounds.forEach((howl) => {
      howl.volume(this.volume);
    });
  }

  getVolume(): number {
    return this.volume;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      Howler.mute(true);
    } else {
      Howler.mute(false);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isUnlocked(): boolean {
    return this.audioUnlocked;
  }

  /**
   * Unlock the Web Audio API AudioContext (required by iOS Safari).
   * Resumes the suspended context and plays a silent buffer to fully activate it.
   */
  unlockAudio(): void {
    if (this.audioUnlocked) return;

    // Resume the Howler AudioContext if it's suspended (iOS Safari suspends it until user gesture)
    const ctx = Howler.ctx;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume();
    }

    // Play and immediately stop a sound to fully unlock the audio pipeline
    const clickSound = this.sounds.get('click');
    if (clickSound) {
      const id = clickSound.play();
      clickSound.stop(id);
    }

    this.audioUnlocked = true;
  }

  // Stop all sounds
  stopAll(): void {
    this.sounds.forEach((howl) => {
      howl.stop();
    });
  }
}

export const audioManager = new AudioManager();

